import { type SiteId } from '@solinkcorp/api-types';
import { type DatePickerLocale, enUS, es, fr } from '@solinkcorp/solink-ui';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import moment from 'moment';
import { LocationStore } from 'stores/LocationStore';

export function resolveTimezone(input: {
  playingLocationId: SiteId | undefined;
  groupLocationId: SiteId | undefined;
  selectedLocationIds: readonly SiteId[];
  timezoneIdOverride: string;
}): string {
  if (input.selectedLocationIds.length > 1) {
    return input.timezoneIdOverride;
  }

  let selectedLocationId: SiteId = ALL_LOCATIONS as SiteId;
  if (input.playingLocationId) {
    selectedLocationId = input.playingLocationId;
  } else if (input.groupLocationId) {
    selectedLocationId = input.groupLocationId;
  } else if (input.selectedLocationIds.length === 1) {
    selectedLocationId = input.selectedLocationIds[0];
  }

  const location = LocationStore.getLocation(selectedLocationId);
  return location?.timezone ?? getLocalTimeZone();
}

/**
 * Left month of the two-month calendar.
 * When `onRight` is true, `focus` sits in the right month.
 * The result is never earlier than the month of `earliest`.
 */
export function leftCalendarMonth(focus: Date, earliest: Date | undefined, onRight: boolean): Date {
  const focusMonth = new Date(focus.getFullYear(), focus.getMonth(), 1);
  const left = onRight
    ? new Date(focusMonth.getFullYear(), focusMonth.getMonth() - 1, 1)
    : focusMonth;
  if (!earliest) {
    return left;
  }
  const earliestMonth = new Date(earliest.getFullYear(), earliest.getMonth(), 1);
  return left.getTime() < earliestMonth.getTime() ? earliestMonth : left;
}

/** Local calendar day whose Y/M/D matches `instant` in `timeZone`. DayPicker selects local days. */
export function calendarDay(instant: Date | number, timeZone: string): Date {
  const zoned = moment.tz(instant, timeZone);
  return new Date(zoned.year(), zoned.month(), zoned.date());
}

/**
 * The picked day is a local Date. Read its Y/M/D as a day in `timeZone`.
 * Keep the current time of day when the header shows a time, so playback does not jump to midnight.
 */
export function instantOnCalendarDay(
  day: Date,
  timeZone: string,
  timeSource: Date | number | undefined,
): Date {
  const source = timeSource === undefined ? undefined : moment.tz(timeSource, timeZone);
  return moment
    .tz(
      {
        year: day.getFullYear(),
        month: day.getMonth(),
        date: day.getDate(),
        hour: source?.hour() ?? 0,
        minute: source?.minute() ?? 0,
        second: source?.second() ?? 0,
      },
      timeZone,
    )
    .toDate();
}

/** Clock time on `instant` in `timeZone`. Hours are 0–23. */
export function readTimeOfDay(instant: number, timeZone: string): { hour: number; minute: number } {
  const zoned = moment.tz(instant, timeZone);
  return { hour: zoned.hour(), minute: zoned.minute() };
}

/** Same calendar day as `instant`, with the clock set to `time`. Seconds go to 0. */
export function withTimeOfDay(
  instant: number,
  timeZone: string,
  time: { hour: number; minute: number },
): number {
  return moment
    .tz(instant, timeZone)
    .hour(time.hour)
    .minute(time.minute)
    .second(0)
    .millisecond(0)
    .valueOf();
}

export interface InstantBounds {
  earliest?: number;
  latest?: number;
}

export interface ClockWindow {
  minHour: number;
  minMinute: number;
  maxHour: number;
  maxMinute: number;
}

/**
 * Guest limits, with `now` as a ceiling.
 * A future instant is not selectable: storing it would play the current time instead.
 */
export function selectableInstantBounds(
  minDate: number | undefined,
  maxDate: number | undefined,
  now: number,
): InstantBounds {
  return {
    earliest: minDate,
    latest: maxDate === undefined ? now : Math.min(maxDate, now),
  };
}

/** Pull `instant` into `bounds`. A future earliest does not move the instant past `latest`. */
export function clampInstant(instant: number, bounds: InstantBounds): number {
  const { earliest, latest } = bounds;
  if (latest !== undefined && earliest !== undefined && earliest > latest) {
    return instant > latest ? latest : instant;
  }
  let next = instant;
  if (earliest !== undefined && next < earliest) {
    next = earliest;
  }
  if (latest !== undefined && next > latest) {
    next = latest;
  }
  return next;
}

function isSamePickerDay(a: number, b: number, timeZone: string): boolean {
  const left = calendarDay(a, timeZone);
  const right = calendarDay(b, timeZone);
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

/** Inclusive clock window for the calendar day of `instant`. A bound on another day does not apply. */
export function clockWindowForDay(
  instant: number,
  timeZone: string,
  bounds: InstantBounds,
): ClockWindow {
  let minHour = 0;
  let minMinute = 0;
  let maxHour = 23;
  let maxMinute = 59;
  if (bounds.earliest !== undefined && isSamePickerDay(instant, bounds.earliest, timeZone)) {
    const earliest = readTimeOfDay(bounds.earliest, timeZone);
    minHour = earliest.hour;
    minMinute = earliest.minute;
  }
  if (bounds.latest !== undefined && isSamePickerDay(instant, bounds.latest, timeZone)) {
    const latest = readTimeOfDay(bounds.latest, timeZone);
    maxHour = latest.hour;
    maxMinute = latest.minute;
  }
  return { minHour, minMinute, maxHour, maxMinute };
}

/** Minutes that can be chosen for `hour`. Empty when that hour is outside the window. */
export function selectableMinutes(hour: number, window: ClockWindow): number[] {
  if (hour < window.minHour || hour > window.maxHour) {
    return [];
  }
  const start = hour === window.minHour ? window.minMinute : 0;
  const end = hour === window.maxHour ? window.maxMinute : 59;
  if (start > end) {
    return [];
  }
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

/** Hours 0–23 that still have a selectable minute. */
export function selectableHours24(window: ClockWindow): number[] {
  const hours: number[] = [];
  for (let hour = window.minHour; hour <= window.maxHour; hour += 1) {
    if (selectableMinutes(hour, window).length > 0) {
      hours.push(hour);
    }
  }
  return hours;
}

/** 12-hour labels in wheel order (1…12) for the given meridiem. */
export function selectable12HourLabels(window: ClockWindow, isPm: boolean): number[] {
  const allowed = new Set(selectableHours24(window).filter((hour) => hour >= 12 === isPm));
  const labels: number[] = [];
  for (let label = 1; label <= 12; label += 1) {
    if (allowed.has(hourOn24HourClock(label, isPm))) {
      labels.push(label);
    }
  }
  return labels;
}

export function selectableMeridiem(window: ClockWindow): { am: boolean; pm: boolean } {
  const hours = selectableHours24(window);
  return {
    am: hours.some((hour) => hour < 12),
    pm: hours.some((hour) => hour >= 12),
  };
}

/** Locale clock string, for example "2:05 PM" or "14:05". */
export function formatClockTime(instant: number, timeZone: string): string {
  return moment.tz(instant, timeZone).format('LT');
}

/** Short zone name on `instant`, for example "EST". */
export function formatTimezone(instant: number, timeZone: string): string {
  return moment.tz(instant, timeZone).format('z');
}

function bareToken(token: string): string {
  return token.replace(/[.,]+$/g, '').toLowerCase();
}

function nameSet(names: string[]): Set<string> {
  return new Set(names.map((name) => bareToken(name)));
}

/** Drop a leading weekday token. "Sun Aug 16" becomes "Aug 16". "Wed, 19 Aug" becomes "19 Aug". */
function stripWeekdayPrefix(segment: string): string {
  const match = segment.match(/^(\S+)\s+([\s\S]+)$/);
  if (!match) {
    return segment;
  }
  const token = bareToken(match[1]);
  const rest = match[2].replace(/^[,\s]+/, '');
  if (!token || !nameSet([...moment.weekdaysShort(), ...moment.weekdays()]).has(token)) {
    return segment;
  }
  // "mar." is both Tuesday and March. "mar. 2026" is a month, so the year stays with it.
  const months = nameSet([...moment.monthsShort(), ...moment.months()]);
  if (months.has(token) && /^\d{4}\b/.test(rest)) {
    return segment;
  }
  return rest;
}

/** "mer., 19 août" becomes "19 août" when the active moment locale has no "mer". */
function dropShortWeekdayComma(segment: string): string {
  const comma = segment.indexOf(',');
  if (comma === -1 || comma > 6) {
    return segment;
  }
  return segment.slice(comma + 1).trim();
}

/**
 * Drop weekday names from a picker label.
 * "Wed, 19 Aug" becomes "19 Aug". "Sun Aug 16 - Sat Aug 22" becomes "Aug 16 - Aug 22".
 */
export function labelWithoutWeekday(dateLabel: string): string {
  return dateLabel
    .split(' - ')
    .map((part) => dropShortWeekdayComma(stripWeekdayPrefix(part)))
    .join(' - ');
}

/** Sidebar label. Clock time is included when the picker edits a time of day. */
export function formatPickerLabel(
  dateLabel: string,
  instant: number,
  timeZone: string,
  showTime: boolean,
): string {
  const zone = formatTimezone(instant, timeZone);
  if (!showTime) {
    return `${dateLabel} ${zone}`;
  }
  return `${dateLabel} ${formatClockTime(instant, timeZone)} ${zone}`;
}

/** True when the active moment locale prints a meridiem in its short time format. */
export function clockUses12Hour(): boolean {
  return /a/i.test(moment.localeData().longDateFormat('LT'));
}

export function hourOn12HourClock(hour24: number): { hour: number; isPm: boolean } {
  return {
    hour: hour24 % 12 === 0 ? 12 : hour24 % 12,
    isPm: hour24 >= 12,
  };
}

export function hourOn24HourClock(hour12: number, isPm: boolean): number {
  if (isPm) {
    return hour12 === 12 ? 12 : hour12 + 12;
  }
  return hour12 === 12 ? 0 : hour12;
}

export function datePickerLocale(localeCode: string): DatePickerLocale {
  if (localeCode.startsWith('fr')) {
    return fr;
  }
  if (localeCode.startsWith('es')) {
    return es;
  }
  return enUS;
}
